if (!customElements.get('google-map')) {
	class GoogleMap extends HTMLElement {
		constructor() {
			super();
			this.container = this.querySelector('.google-map--map');
			const dataset = this.container.dataset;
			this.mapZoom = parseInt(dataset.mapZoom, 10);
			this.mapStyle = dataset.mapStyle;
			this.mapType = dataset.mapType;
			this.panControl = dataset.panControl;
			this.zoomControl = dataset.zoomControl;
			this.mapTypeControl = dataset.maptypeControl || false;
			this.scaleControl = dataset.scaleControl || false;
			this.streetViewControl = dataset.streetviewControl || false;
			this.locations = this.querySelectorAll('.thb-location');
			this.storeItems = this.querySelectorAll('.google-map--store-item');
			this.index = 0;
			this.map = null;
		}

		connectedCallback() {
			if (!window.google) return;

			this.bounds = new google.maps.LatLngBounds();
			this.renderMap();
			this.addEventListeners();
			window.dispatchEvent(new Event('resize'));
		}

		addEventListeners() {
			window.addEventListener('resize', this.mapResize.bind(this));
			google.maps.event.addListenerOnce(this.map, 'tilesloaded', this.onTilesLoaded.bind(this));

			// Collapsible store items
			this.storeItems.forEach((button) => {
				button.addEventListener('click', this.onStoreItemClick.bind(this, button));
			});
		}

		onTilesLoaded() {
			if (this.mapZoom > 0) {
				this.map.setZoom(this.mapZoom);
			} else {
				this.map.fitBounds(this.bounds);
			}
			this.scrollToPin(this.index);
		}

		onStoreItemClick(button) {
			const locationIndex = parseInt(button.dataset.locationIndex, 10);
			const wrapper = button.closest('.google-map--store-item-wrapper');
			const content = wrapper ? wrapper.querySelector('.google-map--store-item-content') : null;
			
			// Close all other items
			this.storeItems.forEach((item) => {
				const itemWrapper = item.closest('.google-map--store-item-wrapper');
				const itemContent = itemWrapper ? itemWrapper.querySelector('.google-map--store-item-content') : null;
				
				if (itemWrapper !== wrapper) {
					item.classList.remove('active');
					if (itemContent) {
						itemContent.classList.remove('active');
					}
				}
			});
			
			// Toggle current item
			if (content) {
				const isActive = button.classList.contains('active');
				
				if (isActive) {
					// Close
					button.classList.remove('active');
					content.classList.remove('active');
				} else {
					// Open
					button.classList.add('active');
					content.classList.add('active');
					
					// Scroll to opened item
					wrapper.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
					
					// Scroll map to pin
					this.scrollToPin(locationIndex);
				}
			}
		}

		getMapOptions() {
			const baseOptions = {
				zoom: this.mapZoom,
				draggable: !("ontouchend" in document),
				scrollwheel: false,
				panControl: this.panControl,
				zoomControl: this.zoomControl,
				mapTypeControl: this.mapTypeControl,
				scaleControl: this.scaleControl,
				streetViewControl: this.streetViewControl,
				fullscreenControl: false,
				mapTypeId: this.mapType,
				gestureHandling: 'cooperative'
			};

			const styles = {
				retro: [
					{
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#ebe3cd"
							}
						]
					},
					{
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#523735"
							}
						]
					},
					{
						"elementType": "labels.text.stroke",
						"stylers": [
							{
								"color": "#f5f1e6"
							}
						]
					},
					{
						"featureType": "administrative",
						"elementType": "geometry.stroke",
						"stylers": [
							{
								"color": "#c9b2a6"
							}
						]
					},
					{
						"featureType": "administrative.land_parcel",
						"elementType": "geometry.stroke",
						"stylers": [
							{
								"color": "#dcd2be"
							}
						]
					},
					{
						"featureType": "administrative.land_parcel",
						"elementType": "labels",
						"stylers": [
							{
								"visibility": "off"
							}
						]
					},
					{
						"featureType": "administrative.land_parcel",
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#ae9e90"
							}
						]
					},
					{
						"featureType": "landscape.natural",
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#dfd2ae"
							}
						]
					},
					{
						"featureType": "poi",
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#dfd2ae"
							}
						]
					},
					{
						"featureType": "poi",
						"elementType": "labels.text",
						"stylers": [
							{
								"visibility": "off"
							}
						]
					},
					{
						"featureType": "poi",
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#93817c"
							}
						]
					},
					{
						"featureType": "poi.park",
						"elementType": "geometry.fill",
						"stylers": [
							{
								"color": "#a5b076"
							}
						]
					},
					{
						"featureType": "poi.park",
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#447530"
							}
						]
					},
					{
						"featureType": "road",
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#f5f1e6"
							}
						]
					},
					{
						"featureType": "road.arterial",
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#fdfcf8"
							}
						]
					},
					{
						"featureType": "road.highway",
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#f8c967"
							}
						]
					},
					{
						"featureType": "road.highway",
						"elementType": "geometry.stroke",
						"stylers": [
							{
								"color": "#e9bc62"
							}
						]
					},
					{
						"featureType": "road.highway.controlled_access",
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#e98d58"
							}
						]
					},
					{
						"featureType": "road.highway.controlled_access",
						"elementType": "geometry.stroke",
						"stylers": [
							{
								"color": "#db8555"
							}
						]
					},
					{
						"featureType": "road.local",
						"elementType": "labels",
						"stylers": [
							{
								"visibility": "off"
							}
						]
					},
					{
						"featureType": "road.local",
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#806b63"
							}
						]
					},
					{
						"featureType": "transit.line",
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#dfd2ae"
							}
						]
					},
					{
						"featureType": "transit.line",
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#8f7d77"
							}
						]
					},
					{
						"featureType": "transit.line",
						"elementType": "labels.text.stroke",
						"stylers": [
							{
								"color": "#ebe3cd"
							}
						]
					},
					{
						"featureType": "transit.station",
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#dfd2ae"
							}
						]
					},
					{
						"featureType": "water",
						"elementType": "geometry.fill",
						"stylers": [
							{
								"color": "#b9d3c2"
							}
						]
					},
					{
						"featureType": "water",
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#92998d"
							}
						]
					}
				],
				night: [
					{
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#242f3e"
							}
						]
					},
					{
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#746855"
							}
						]
					},
					{
						"elementType": "labels.text.stroke",
						"stylers": [
							{
								"color": "#242f3e"
							}
						]
					},
					{
						"featureType": "administrative.land_parcel",
						"elementType": "labels",
						"stylers": [
							{
								"visibility": "off"
							}
						]
					},
					{
						"featureType": "administrative.locality",
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#d59563"
							}
						]
					},
					{
						"featureType": "poi",
						"elementType": "labels.text",
						"stylers": [
							{
								"visibility": "off"
							}
						]
					},
					{
						"featureType": "poi",
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#d59563"
							}
						]
					},
					{
						"featureType": "poi.park",
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#263c3f"
							}
						]
					},
					{
						"featureType": "poi.park",
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#6b9a76"
							}
						]
					},
					{
						"featureType": "road",
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#38414e"
							}
						]
					},
					{
						"featureType": "road",
						"elementType": "geometry.stroke",
						"stylers": [
							{
								"color": "#212a37"
							}
						]
					},
					{
						"featureType": "road",
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#9ca5b3"
							}
						]
					},
					{
						"featureType": "road.highway",
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#746855"
							}
						]
					},
					{
						"featureType": "road.highway",
						"elementType": "geometry.stroke",
						"stylers": [
							{
								"color": "#1f2835"
							}
						]
					},
					{
						"featureType": "road.highway",
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#f3d19c"
							}
						]
					},
					{
						"featureType": "road.local",
						"elementType": "labels",
						"stylers": [
							{
								"visibility": "off"
							}
						]
					},
					{
						"featureType": "transit",
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#2f3948"
							}
						]
					},
					{
						"featureType": "transit.station",
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#d59563"
							}
						]
					},
					{
						"featureType": "water",
						"elementType": "geometry",
						"stylers": [
							{
								"color": "#17263c"
							}
						]
					},
					{
						"featureType": "water",
						"elementType": "labels.text.fill",
						"stylers": [
							{
								"color": "#515c6d"
							}
						]
					},
					{
						"featureType": "water",
						"elementType": "labels.text.stroke",
						"stylers": [
							{
								"color": "#17263c"
							}
						]
					}
				]
			};

			if (styles[this.mapStyle]) {
				baseOptions.styles = styles[this.mapStyle];
			}

			return baseOptions;
		}

		renderMap() {
			const mapOptions = this.getMapOptions();
			this.map = new google.maps.Map(this.container, mapOptions);
			this.locations.forEach((location, i) => {
				this.renderMarker(i, location);
				location.dataset.rendered = 'true';
			});
		}

		mapResize() {
			setTimeout(() => {
				this.scrollToPin(this.index);
			}, 50);
		}

		renderMarker(index, location) {
			const options = JSON.parse(location.dataset.option);
			const latlng = new google.maps.LatLng(options.latitude, options.longitude);
			location.dataset.latlng = latlng;
			this.bounds.extend(latlng);

			const pinimageLoad = new Image();
			pinimageLoad.src = options.marker_image;

			pinimageLoad.addEventListener('load', () => {
				this.setMarker(index, latlng, options);
			});
		}

		setMarker(index, latlng, options) {
			const markerSize = options.retina_marker && !options.rendered ? [options.marker_size[0] / 2, options.marker_size[1] / 2] : options.marker_size;
			const location = this.locations[index];
			const locationData = location.dataset;
			const locationInfo = {
				title: options.title || '',
				description: options.description || '',
				latitude: options.latitude,
				longitude: options.longitude
			};
			const mapElement = this; // Reference to GoogleMap custom element

			class CustomMarker extends google.maps.OverlayView {
				constructor(latlng, map, locationInfo, index, mapElement) {
					super();
					this.latlng = latlng;
					this.mapInstance = map;
					this.mapElement = mapElement;
					this.locationInfo = locationInfo;
					this.index = index;
					this.tooltip = null;
					this.setMap(map);
				}

				createTooltip() {
					if (this.tooltip) return;

					this.tooltip = document.createElement('div');
					this.tooltip.className = 'thb-pin-tooltip';
					
					// Parse description HTML/text
					let descriptionHtml = '';
					if (this.locationInfo.description) {
						// If description contains HTML, use it; otherwise convert newlines to <br>
						if (this.locationInfo.description.includes('<')) {
							// Create temporary div to extract text content from HTML
							const tempDiv = document.createElement('div');
							tempDiv.innerHTML = this.locationInfo.description;
							descriptionHtml = tempDiv.innerHTML;
						} else {
							descriptionHtml = this.locationInfo.description.replace(/\n/g, '<br>');
						}
					}

					// Create tooltip content structure
					const titleHtml = this.locationInfo.title ? `<div class="thb-pin-tooltip-title">${this.locationInfo.title}</div>` : '';
					
					// Parse description to extract contact info if available
					let contentHtml = '';
					if (descriptionHtml) {
						// Create temporary div to parse HTML
						const tempDiv = document.createElement('div');
						tempDiv.innerHTML = descriptionHtml;
						
						// Check if description contains structured info (p tags, br tags, etc.)
						const paragraphs = tempDiv.querySelectorAll('p');
						let htmlContent = '';
						
						if (paragraphs.length > 0) {
							paragraphs.forEach((p, idx) => {
								if (idx < paragraphs.length - 1) {
									htmlContent += `<p>${p.innerHTML}</p>`;
								} else {
									htmlContent += `<p>${p.innerHTML}</p>`;
								}
							});
						} else {
							// Plain text - convert line breaks
							htmlContent = descriptionHtml;
						}
						
						contentHtml = `<div class="thb-pin-tooltip-content">${htmlContent}</div>`;
					}
					
					// Create directions button
					const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${this.locationInfo.latitude},${this.locationInfo.longitude}`;
					const directionsButton = `<a href="${directionsUrl}" target="_blank" class="thb-pin-tooltip-directions" rel="noopener noreferrer">Yol Tarifi Al</a>`;
					
					this.tooltip.innerHTML = `
						<div class="thb-pin-tooltip-inner">
							${titleHtml}
							${contentHtml}
							<div class="thb-pin-tooltip-footer">${directionsButton}</div>
						</div>
					`;
					
					// Enable pointer events for interactive elements
					this.tooltip.style.pointerEvents = 'none';
					this.tooltip.style.position = 'absolute';
					this.tooltip.style.transform = 'none'; // Reset any transforms from CSS
					this.tooltip.style.margin = '0'; // Reset margins
					
					// Allow tooltip to stay open when hovering over it
					let tooltipHideTimeout = null;
					
					this.tooltip.addEventListener('mouseenter', () => {
						if (tooltipHideTimeout) {
							clearTimeout(tooltipHideTimeout);
							tooltipHideTimeout = null;
						}
					});
					
					this.tooltip.addEventListener('mouseleave', () => {
						tooltipHideTimeout = setTimeout(() => {
							this.hideTooltip();
						}, 100);
					});
					
					// Tooltip will be appended to overlay pane in showTooltip() when needed
				}

				showTooltip(point) {
					if (!this.tooltip) this.createTooltip();
					if (!this.tooltip || (!this.locationInfo.title && !this.locationInfo.description)) return;

					// Get overlay pane to append tooltip
					const panes = this.getPanes();
					if (!panes) return;
					
					// Append tooltip to overlay pane if not already appended
					const overlayPane = panes.overlayImage;
					if (!this.tooltip.parentNode || this.tooltip.parentNode !== overlayPane) {
						overlayPane.appendChild(this.tooltip);
					}

					// Force tooltip to render to get dimensions
					this.tooltip.style.display = 'block';
					this.tooltip.style.visibility = 'hidden';
					this.tooltip.classList.add('visible');
					
					// Force reflow to get accurate dimensions
					void this.tooltip.offsetWidth;
					
					const tooltipWidth = this.tooltip.offsetWidth || 320;
					const tooltipHeight = this.tooltip.offsetHeight || 150;
					
					// Get overlay pane dimensions for boundary checking
					const paneWidth = overlayPane.offsetWidth || overlayPane.clientWidth;
					const paneHeight = overlayPane.offsetHeight || overlayPane.clientHeight;
					
					// Marker positioning (from draw method):
					// - Marker div left: point.x - (markerSize[0] / 2)
					// - Marker div top: point.y - (markerSize[1] / 2)
					// - Marker center is at point.x, point.y
					// - Marker top edge: point.y - (markerSize[1] / 2)
					// - Marker bottom edge: point.y + (markerSize[1] / 2)
					
					// Position tooltip above marker
					// Tooltip's bottom edge should be 12px above marker's top edge
					// Tooltip top = marker top - tooltip height - gap
					const markerTopEdge = point.y - (markerSize[1] / 2);
					let left = point.x - (tooltipWidth / 2);
					let top = markerTopEdge - tooltipHeight - 12;
					
					// Adjust horizontal position if tooltip goes off screen
					let adjustedLeft = left;
					const leftMin = 10;
					const leftMax = paneWidth - tooltipWidth - 10;
					
					if (adjustedLeft < leftMin) {
						adjustedLeft = leftMin;
					} else if (adjustedLeft > leftMax && leftMax > leftMin) {
						adjustedLeft = leftMax;
					}
					
					// Position below marker if no space above
					let isBelow = false;
					let adjustedTop = top;
					
					if (adjustedTop < 10) {
						// Not enough space above, position below marker
						adjustedTop = point.y + (markerSize[1] / 2) + 12;
						isBelow = true;
						this.tooltip.classList.add('tooltip-below');
					} else {
						this.tooltip.classList.remove('tooltip-below');
					}

					// Adjust arrow position to point to marker center
					// Calculate where marker center is relative to tooltip's left edge
					const markerCenterFromTooltipLeft = point.x - adjustedLeft;
					
					if (tooltipWidth > 0 && markerCenterFromTooltipLeft >= 0 && markerCenterFromTooltipLeft <= tooltipWidth) {
						// Marker center is within tooltip bounds
						const markerCenterPercent = (markerCenterFromTooltipLeft / tooltipWidth) * 100;
						this.tooltip.style.setProperty('--arrow-position', `${Math.max(15, Math.min(85, markerCenterPercent))}%`);
					} else {
						// Fallback to center
						this.tooltip.style.setProperty('--arrow-position', '50%');
					}

					// Position relative to overlay pane (same coordinate system as marker)
					this.tooltip.style.left = `${adjustedLeft}px`;
					this.tooltip.style.top = `${adjustedTop}px`;
					this.tooltip.style.visibility = 'visible';
					this.tooltip.style.pointerEvents = 'auto';
					
					// Store last point for updates
					this.lastTooltipPoint = point;
				}

				hideTooltip() {
					if (this.tooltip) {
						this.tooltip.classList.remove('visible');
						this.tooltip.style.visibility = 'hidden';
						this.tooltip.style.display = 'none';
						this.tooltip.style.pointerEvents = 'none';
					}
				}

				draw() {
					if (!this.div_) {
						this.div_ = document.createElement('div');
						this.div_.classList.add('thb-pin');
						this.div_.innerHTML = `
							<div class="pulse"></div>
							<div class="shadow"></div>
							<div class="pin-wrap"><img src="${options.marker_image}" width="${markerSize[0]}" height="${markerSize[1]}" /></div>`;
						this.div_.style.position = 'absolute';
						this.div_.style.cursor = 'pointer';

						const panes = this.getPanes();
						panes.overlayImage.appendChild(this.div_);

						// Click handler - opens corresponding store item in list
						this.div_.addEventListener('click', () => {
							if (this.mapElement) {
								const storeItems = this.mapElement.querySelectorAll('.google-map--store-item');
								const targetButton = storeItems[this.index];
								
								if (targetButton) {
									// Trigger click on store item to open/close it
									targetButton.click();
								}
							}
						});

						// Tooltip disabled - no hover handlers needed
					}

					const point = this.getProjection().fromLatLngToDivPixel(this.latlng);
					if (point) {
						const shadowOffset = (markerSize[0] - 39) / 2;
						this.div_.style.left = `${point.x - (markerSize[0] / 2)}px`;
						this.div_.style.top = `${point.y - (markerSize[1] / 2)}px`;
						this.div_.style.width = `${markerSize[0]}px`;
						this.div_.style.height = `${markerSize[1]}px`;
						
						if (this.div_.querySelector('.shadow')) {
							this.div_.querySelector('.shadow').style.marginLeft = `${shadowOffset}px`;
						}
						if (this.div_.querySelector('.pulse')) {
							this.div_.querySelector('.pulse').style.marginLeft = `${shadowOffset}px`;
						}

						// Tooltip disabled - no position updates needed
					}
				}

				getPosition() {
					return this.latlng;
				}

				onRemove() {
					if (this.tooltip) {
						this.hideTooltip();
						if (this.tooltip.parentNode) {
							this.tooltip.parentNode.removeChild(this.tooltip);
						}
					}
				}
			}

			const marker = new CustomMarker(latlng, this.map, locationInfo, index, mapElement);
			// Store marker reference for cleanup if needed
			if (!this.markers) this.markers = [];
			this.markers.push(marker);
		}

		scrollToPin(index) {
			const location = this.locations[index];
			const options = JSON.parse(location.dataset.option);
			const latlng = new google.maps.LatLng(options.latitude, options.longitude);
			this.map.setZoom(this.mapZoom);
			this.map.panTo(latlng);
		}
	}

	customElements.define('google-map', GoogleMap);
}

